// ─────────────────────────────────────────────────────────────────────────
// useTeamsStatus — single source of truth for "is Teams connected?" in
// the frontend. Polls /cima/microsoft/config/status on mount + every 60s
// (only when the page is visible) so the banner clears the moment an
// admin completes the setup form.
//
// Caches in localStorage so the gate UX is correct on first paint even
// before the API responds. Stale-but-renderable beats blank-loading.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react';
import { getStatus, MicrosoftConfigStatus } from '../api/microsoftConfig';

const LS_KEY = 'cima-teams-status';

function readLocal(): MicrosoftConfigStatus | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeLocal(s: MicrosoftConfigStatus) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch {}
}

export function useTeamsStatus() {
  const [status,  setStatus]  = useState<MicrosoftConfigStatus | null>(() => readLocal());
  const [loaded,  setLoaded]  = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await getStatus();
      setStatus(s);
      writeLocal(s);
    } catch {
      // Network failure — keep last-known status, don't flip ready state
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 60_000);
    return () => clearInterval(interval);
  }, [refresh]);

  return {
    status,
    loaded,
    refresh,
    // Convenience accessors
    isReady:      !!status?.ready,
    isConfigured: !!status?.configured,
  };
}
